import React, { useEffect, useRef, useState } from "react";
import { Loader2, Bell, BellOff, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AttendeeMiniCard } from "./AttendeeMiniCard";

/**
 * LiveWall — polls the event tap-in feed every 5 s and fires a foreground
 * browser Notification for each fresh arrival (only when the tab is hidden).
 */
export function LiveWall({ eventId }) {
  const [tapins, setTapins]   = useState([]);
  const [total,  setTotal]    = useState(0);
  const [live,   setLive]     = useState(true);
  const [notif,  setNotif]    = useState(typeof Notification !== "undefined" ? Notification.permission : "denied");
  const sinceRef  = useRef(null);
  const timerRef  = useRef(null);
  const seededRef = useRef(false);

  const enableNotifs = async () => {
    if (typeof Notification === "undefined") { toast.error("This browser doesn't support notifications"); return; }
    try {
      const perm = await Notification.requestPermission();
      setNotif(perm);
      if (perm === "granted") toast.success("Notifications enabled — you'll see each tap-in live");
      else if (perm === "denied") toast.error("Notifications blocked — change site settings to re-enable");
    } catch { toast.error("Couldn't request permission"); }
  };

  const disableNotifs = () => { setNotif("denied-manual"); toast.info("Notifications paused for this session"); };

  const fireNotification = (t) => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    if (notif === "denied-manual") return;
    if (document.visibilityState === "visible") return;
    try {
      const n = new Notification(`🔔 ${t.user_name || "Someone"} just tapped in`, {
        body: t.note || "New arrival at the event",
        icon: t.user_pic || "/icons/find2near.png",
        tag:  `event-${eventId}-${t.tapin_id}`,
      });
      n.onclick = () => { window.focus(); n.close(); };
    } catch { /* silent */ }
  };

  const poll = async () => {
    try {
      const params = sinceRef.current ? { since: sinceRef.current } : {};
      const r = await api.get(`/nfc/events/${eventId}/tap-ins`, { params });
      setTotal(r.data.total || 0);
      sinceRef.current = r.data.now || sinceRef.current;
      if (r.data.tapins?.length) {
        setTapins(prev => {
          const merged = [...r.data.tapins, ...prev];
          return [...new Map(merged.map(t => [t.tapin_id, t])).values()].slice(0, 100);
        });
        if (seededRef.current) r.data.tapins.forEach(fireNotification);
      }
      seededRef.current = true;
    } catch { /* silent */ }
  };

  useEffect(() => {
    poll();
    if (live) timerRef.current = setInterval(poll, 5000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, live]);

  const notifBtn = notif === "granted"
    ? { label: "Notifications on", cls: "bg-emerald-100 text-emerald-800 border-emerald-300", I: Bell, onClick: disableNotifs, testid: "notif-disable" }
    : { label: notif === "denied" ? "Notifications blocked" : "Enable notifications",
        cls: "bg-white text-stone-700 border-stone-200 hover:border-emerald-400",
        I: notif === "denied" ? BellOff : Bell, onClick: enableNotifs, testid: "notif-enable" };

  return (
    <section data-testid="event-live-wall" className="rounded-3xl bg-white border border-stone-200 overflow-hidden">
      <div className="px-4 py-2 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-stone-200 flex items-center gap-2 flex-wrap">
        <span className={`w-2 h-2 rounded-full ${live ? "bg-emerald-500 animate-pulse" : "bg-stone-300"}`}/>
        <span className="text-[11px] uppercase tracking-widest font-black text-stone-700">Live · who just tapped in</span>
        <span className="text-[11px] text-stone-500 ml-auto">{total} check-ins</span>
        <button
          onClick={notifBtn.onClick}
          disabled={notif === "denied"}
          className={`text-[11px] font-black rounded-full px-2.5 py-1 border inline-flex items-center gap-1 ${notifBtn.cls}`}
          data-testid={notifBtn.testid}
          title="Get a desktop alert whenever someone new taps in"
        >
          <notifBtn.I size={11}/> {notifBtn.label}
        </button>
        <button onClick={() => setLive(l => !l)} className="text-[11px] font-black text-emerald-700 hover:underline" data-testid="live-wall-toggle">
          {live ? "Pause" : "Resume"}
        </button>
      </div>
      {tapins.length === 0 ? (
        <div className="p-6 text-center text-sm text-stone-500">Nobody has tapped in yet — the wall will populate as attendees arrive.</div>
      ) : (
        <ul className="divide-y divide-stone-100 max-h-96 overflow-y-auto">
          {tapins.map((t, i) => <TapinRow key={t.tapin_id} tapin={t} eventId={eventId} isTop={i === 0}/>)}
        </ul>
      )}
    </section>
  );
}

function TapinRow({ tapin: t, eventId, isTop }) {
  const [detail, setDetail]  = useState(null);
  const [loading, setLoading] = useState(false);
  const open = async () => {
    if (detail !== null) { setDetail(null); return; }
    setLoading(true);
    try {
      const r = await api.get(`/nfc/events/${eventId}/attendees`, { params: { q: t.user_name, limit: 5 } });
      const hit = (r.data.attendees || []).find(a => a.user_id === t.user_id) || r.data.attendees?.[0];
      setDetail(hit || { user_name: t.user_name });
    } catch { setDetail({ user_name: t.user_name }); }
    finally { setLoading(false); }
  };
  return (
    <li className="animate-in fade-in slide-in-from-top-2" data-testid={`live-tapin-${t.tapin_id}`}>
      <button onClick={open} className="w-full p-3 flex items-center gap-3 text-left hover:bg-stone-50">
        {t.user_pic ? (
          <img src={t.user_pic} alt={t.user_name} className="w-9 h-9 rounded-full object-cover"/>
        ) : (
          <div className="w-9 h-9 rounded-full bg-emerald-50 grid place-items-center text-emerald-700 font-black text-sm">
            {(t.user_name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-bold text-stone-900 truncate">{t.user_name}</div>
          <div className="text-[11px] text-stone-500 truncate">{t.note || "checked in"}</div>
        </div>
        <div className="text-[10px] font-mono text-stone-400 shrink-0">{(t.created_at || "").slice(11, 16)}</div>
        {isTop && <Sparkles size={13} className="text-emerald-600 shrink-0"/>}
      </button>
      {loading && <div className="px-4 pb-3 text-[11px] text-stone-500"><Loader2 size={11} className="inline animate-spin mr-1"/> Loading tap-card…</div>}
      {detail && <div className="px-4 pb-3"><AttendeeMiniCard att={detail}/></div>}
    </li>
  );
}
